if(!customElements.get('custom-cart')){
 class CustomCart extends HTMLElement {
    constructor(){
        super();

        this.sectionId = this.dataset.id;

        // all cart mutations (quantity changes, removals) run through this chain
        // one at a time - concurrent /cart/change requests can be rejected by
        // Shopify or resolve out of order (same pattern as cart-drawer.js)
        this.cartMutationChain = Promise.resolve();
        this.loadingCount = 0;

        this.init();

        // an item was added from anywhere on the page (e.g. quick-add in the
        // cart drawer's recommendations) - re-render so the list includes it
        document.addEventListener('cart:item-added', () => {
            this.refreshFromServer();
        });
    }

    queueMutation(task){
        var run = this.cartMutationChain.catch(() => {}).then(task);
        this.cartMutationChain = run.catch(() => {});
        return run;
    }

    // the loading overlay reflects the whole chain, not one task - counted, so an
    // early task finishing can't lift it while later tasks are still queued
    setLoading(isLoading){
        this.loadingCount += isLoading? 1 : -1;
        if(this.loadingCount < 0) this.loadingCount = 0;

        var active = this.loadingCount > 0;
        if(this.loading) this.loading.setAttribute('data-hidden', active? 'false' : 'true');
        this.setAttribute('data-loading-status', active? 'true' : 'false');
    }

    // abort a request that hangs instead of wedging the mutation chain forever
    requestSignal(){
        return (typeof AbortSignal !== 'undefined' && AbortSignal.timeout)? AbortSignal.timeout(15000) : undefined;
    }

    init(){
        this.btnItemRemoves = this.querySelectorAll('.btn-item-remove');
        this.btnItemRemoves.forEach(item => {
            item.addEventListener('click', this.clickItemRemove.bind(this));
        })

        this.cartItems = this.querySelector('.cart-items');
        this.subTotals = this.querySelector('.cart-subtotal');

        this.loading = this.querySelector('.loading');

        this.quantitySelectors = this.querySelectorAll('quantity-selector');
        this.quantitySelectors.forEach(item => {
            var btnRemoveQuantity = item.querySelector('[data-action="remove"]');
            var btnAddQuantity = item.querySelector('[data-action="add"]');
            var input = item.querySelector('.input-quantity');

            // coalesce rapid +/- clicks and typing into a single request:
            // the displayed value updates instantly (handled by the shared
            // quantity-selector element), the request fires once the user pauses.
            // input.debounceTimer doubles as the "edit pending" flag.
            var queueUpdate = () => {
                clearTimeout(input.debounceTimer);
                input.debounceTimer = setTimeout(() => {
                    input.debounceTimer = null;
                    this.updateQuantity({ currentTarget: input });
                }, 400);
            };

            btnRemoveQuantity.addEventListener('click', queueUpdate);
            btnRemoveQuantity.addEventListener('keydown', e => {
                if(e.key === 'Enter'){
                    queueUpdate();
                }
            });
            btnAddQuantity.addEventListener('click', queueUpdate);
            btnAddQuantity.addEventListener('keydown', e => {
                if(e.key === 'Enter'){
                    queueUpdate();
                }
            });

            input.addEventListener('input', queueUpdate);

            // restore the last committed value if the field is left empty
            input.addEventListener('change', () => {
                if(input.value.trim() === '' || isNaN(parseInt(input.value))){
                    input.value = input.getAttribute('value');
                }
            });
        })

        this.errorMsgs = this.querySelectorAll('.cart-item__errors');

        this.adjustMarginTop();
    }

    refresh(){
        this.init();
    }

    adjustMarginTop(){
        var header = document.querySelector('custom-header');

        if(header){
            const position = window.getComputedStyle(header).position;
            if(position === 'absolute'){
                var headerHeight = header.offsetHeight;
                this.querySelector('.custom-container').style.marginTop = `${ headerHeight }px`;
            }
        }
    }

    // commit any debounced quantity edits that haven't been sent yet, so a removal
    // can't discard them. Edits inside exceptItem are dropped instead - that line
    // is about to be removed anyway.
    flushPendingQuantityUpdates(exceptItem){
        this.querySelectorAll('.input-quantity').forEach(input => {
            if(!input.debounceTimer) return;
            clearTimeout(input.debounceTimer);
            input.debounceTimer = null;
            if(exceptItem && exceptItem.contains(input)) return;
            this.updateQuantity({ currentTarget: input });
        });
    }

    updateQuantity(e){
        var btn = e.currentTarget;
        var input = btn.closest('.quantity-wrapper').querySelector('input[name="quantity"]');
        var quantity = input.value;
        var itemKey = input.dataset.key;
        var itemLine = input.dataset.idx;
        var itemId = input.dataset.id;

        // ignore empty values while typing - the change listener restores them on blur
        if(quantity.trim() === '' || isNaN(parseInt(quantity))) return;

        var maxQuantity = parseInt(input.dataset.max);

        // correct values below the minimum to 1 (unless the item is out of stock;
        // removing an item goes through the remove link instead)
        if(parseInt(quantity) < 1){
            if(!isNaN(maxQuantity) && maxQuantity < 1) return;
            quantity = '1';
            input.value = 1;
            input.setAttribute('value', 1);
        }

        // clamp to the available inventory when the variant is stock-tracked
        if(!isNaN(maxQuantity) && maxQuantity > 0 && parseInt(quantity) > maxQuantity){
            quantity = `${maxQuantity}`;
            input.value = maxQuantity;
            input.setAttribute('value', maxQuantity);

            var maxMsg = (window.smallText && window.smallText.maxQuantityText)? window.smallText.maxQuantityText.replace('[QTY]', maxQuantity) : `Only ${maxQuantity} in stock.`;
            var errorWrapper = input.closest('.cart-item').querySelector('.cart-item__errors');
            if(errorWrapper){
                errorWrapper.querySelector('.error').innerHTML = maxMsg;
                errorWrapper.setAttribute('data-hidden', 'false');
                errorWrapper.dataset.info = 'true';
            }
        }

        this.setItemSpinner(itemKey, itemLine, true);

        // capture the validated values now - the task may run later, after the
        // input was edited again or the list re-rendered. The line item key stays
        // valid across re-renders and removals, unlike positional line numbers.
        var requestQuantity = `${quantity}`;
        this.queueMutation(() => {
            // the row may have been re-rendered while this task waited - read the
            // live line/key when the input is still attached
            var line = (input.isConnected && input.dataset.idx)? input.dataset.idx : itemLine;
            var key = (input.isConnected && input.dataset.key)? input.dataset.key : itemKey;

            var formData = new FormData();
            // address by LINE first: this store's cart app regenerates line item
            // keys after every change, so a stored key is dead by the second
            // request ("no valid id or line parameter"). Line numbers stay valid -
            // the page fully re-renders (fresh data-idx) after any removal.
            if(line){
                formData.append("line", line);
            }else if(key){
                formData.append("id", key);
            }else{
                formData.append("id", itemId);
            }
            formData.append("quantity", requestQuantity);
            formData.append("sections_url", window.location.pathname);
            formData.append("sections", [this.sectionId, 'header-cart-icon']);

            return this.submitToCart(formData, key, line);
        });
    }

    // selector for one cart line - by line number first (item keys rotate on
    // every change on this store, so a stored key may already be stale)
    itemSelector(key, line){
        return line? `.cart-item[data-idx="${line}"]` : `.cart-item[data-key="${key}"]`;
    }

    setItemSpinner(key, line, isLoading){
        var spinner = this.querySelector(`${this.itemSelector(key, line)} .heading .spinner`);
        if(spinner) spinner.setAttribute('data-loading-status', isLoading? 'true' : 'false');
    }

    submitToCart(formData, key, line){
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;
        config.signal = this.requestSignal();

        return fetch(`${routes.cart_change_url}`, config)
        .then(res => res.json())
        .then(res => {
            // remove all error messages (informational ones, e.g. the inventory
            // clamp notice, survive one response so the shopper can read them)
            this.errorMsgs.forEach(item => {
                if(item.dataset.info == 'true'){
                    delete item.dataset.info;
                    return;
                }
                item.setAttribute('data-hidden', 'true');
                item.querySelector('.error').innerHTML = '';
            })

            var currentItem = this.querySelector(this.itemSelector(key, line));

            if(res.status){
                if(!currentItem){
                    // the row this edit targeted no longer matches the DOM -
                    // reload so the page resyncs with the live cart
                    window.location.reload();
                }
                if(currentItem){
                    currentItem.querySelector('.cart-item__errors .error').innerHTML = `${res.message}`;
                    currentItem.querySelector('.cart-item__errors').setAttribute('data-hidden', 'false');

                    // revert the optimistic quantity change
                    var currentQuantitySelector = currentItem.querySelector('quantity-selector');
                    if(currentQuantitySelector) currentQuantitySelector.removeQuantity();
                }

            }else if(res.token){
                const pageHTML = new DOMParser().parseFromString(res.sections[this.sectionId], 'text/html');

                // update subtotal price
                const cartSummary = pageHTML.querySelector('.cart-summary');
                if(cartSummary && this.subTotals){
                    this.subTotals.innerHTML = cartSummary.querySelector('.cart-subtotal').innerHTML;
                }

                // update this item's prices + committed input value, matched by key
                // so it targets the right line even after other lines were removed
                const cartItem = pageHTML.querySelector(this.itemSelector(key, line));
                if(cartItem && currentItem){
                    // the change regenerated this line's item key - sync the fresh
                    // one onto the live row so key-based fallbacks stay usable
                    if(cartItem.dataset.key){
                        currentItem.dataset.key = cartItem.dataset.key;
                        var liveInput = currentItem.querySelector('.input-quantity');
                        if(liveInput) liveInput.dataset.key = cartItem.dataset.key;
                    }
                    ['.cart-item__final-price', '.cart-item__discounts', '.cart-item__total-price'].forEach(selector => {
                        var currentPart = currentItem.querySelector(selector);
                        var newPart = cartItem.querySelector(selector);
                        if(currentPart && newPart) currentPart.innerHTML = newPart.innerHTML;
                    });

                    var currentInput = currentItem.querySelector('.input-quantity');
                    var newInput = cartItem.querySelector('.input-quantity');
                    if(currentInput && newInput){
                        currentInput.value = newInput.value;
                        currentInput.setAttribute('value', newInput.value);
                    }
                }

                if(res.sections['header-cart-icon']){
                    this.updateCartCount(res.sections['header-cart-icon']);
                }
            }

            this.setItemSpinner(key, line, false);
        })
        .catch(() => {
            // network failure/timeout - stop the spinner so the item is not stuck loading
            this.setItemSpinner(key, line, false);
        });
    }

    clickItemRemove(e){
        e.preventDefault();

        var removedItem = e.currentTarget.closest('.cart-item');
        var itemKey = removedItem.dataset.key;
        var itemLine = removedItem.dataset.idx;

        // send any not-yet-sent quantity edits first (dropping the one for the
        // line being removed - that line is about to disappear anyway)
        this.flushPendingQuantityUpdates(removedItem);

        this.setLoading(true);

        this.queueMutation(() => this.removeItem(itemKey, itemLine));
    }

    removeItem(key, line){
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.signal = this.requestSignal();

        var formData = new FormData();
        // line first - item keys rotate on every change on this store
        if(line){
            formData.append("line", line);
        }else{
            formData.append("id", key);
        }
        formData.append("quantity", '0');
        formData.append("sections_url", window.location.pathname);
        formData.append("sections", [this.sectionId, 'header-cart-icon']);
        config.body = formData;

        return fetch(`${routes.cart_change_url}`, config)
        .then(res => res.json())
        .then(res => {
            if(res.sections && res.sections[this.sectionId]){
                this.renderCartSection(res.sections[this.sectionId]);
            }

            if(res.sections && res.sections['header-cart-icon']){
                this.updateCartCount(res.sections['header-cart-icon']);
            }
        })
        .catch(error => {
            console.error(error);
        })
        .finally(() => {
            this.setLoading(false);
        });
    }

    // swap the page's cart markup for a freshly rendered copy of this section
    renderCartSection(sectionText){
        const cartWrapper = new DOMParser().parseFromString(sectionText, 'text/html').querySelector('custom-cart');
        if(!cartWrapper) return;

        // full swap when the cart is (or was) empty - the surgical targets
        // (.cart-items / .cart-subtotal) don't exist in the empty state
        if(cartWrapper.dataset.numItems == '0' || !this.cartItems || !this.subTotals){
            this.innerHTML = cartWrapper.innerHTML;

            if(cartWrapper.dataset.numItems == '0'){
                this.classList.remove('d-block');
                this.classList.add('d-flex');
                this.classList.add('justify-content-center');
                this.classList.add('align-items-center');
            }else{
                this.classList.add('d-block');
                this.classList.remove('d-flex');
                this.classList.remove('justify-content-center');
                this.classList.remove('align-items-center');
            }
        }else{
            this.cartItems.innerHTML = cartWrapper.querySelector('.cart-items').innerHTML;
            this.subTotals.querySelector('.total-value').innerHTML = cartWrapper.querySelector('.cart-subtotal .total-value').innerHTML;

            // swap the gift wrapping element for the fresh copy so its checkbox
            // reflects the real cart state and isn't left disabled after a toggle
            const giftWrap = this.querySelector('cart-gift-wrapping');
            const freshGiftWrap = cartWrapper.querySelector('cart-gift-wrapping');
            if(giftWrap && freshGiftWrap){
                giftWrap.replaceWith(freshGiftWrap);
            }
            this.classList.add('d-block');
            this.classList.remove('d-flex');
            this.classList.remove('justify-content-center');
            this.classList.remove('align-items-center');
        }

        this.refresh();
    }

    // re-fetch this section so the page reflects a cart change made elsewhere
    // (e.g. a product added from the cart drawer's recommendations, or the
    // gift wrapping checkbox); also refreshes the header cart count
    refreshFromServer(){
        this.setLoading(true);

        this.queueMutation(() =>
            fetch(`${window.location.pathname}?sections=${this.sectionId},header-cart-icon`, { signal: this.requestSignal() })
            .then(res => res.json())
            .then(res => {
                if(res[this.sectionId]){
                    this.renderCartSection(res[this.sectionId]);
                }
                if(res['header-cart-icon']){
                    this.updateCartCount(res['header-cart-icon']);
                }
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                this.setLoading(false);
            })
        );
    }

    updateCartCount(sectionText){
        const countSection = new DOMParser().parseFromString(sectionText, 'text/html').querySelector('#shopify-section-header-cart-icon');
        const cartCount = document.querySelector('custom-header .nav-item .cart-count');

        if(countSection && cartCount){
            var count = countSection.textContent.trim();
            cartCount.innerHTML = count;
            cartCount.setAttribute('data-hidden', (parseInt(count) > 0)? 'false' : 'true');
        }
    }
 }

 customElements.define('custom-cart', CustomCart);
}
